import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { 
  Truck, 
  TrendingUp,
  TrendingDown,
  AlertTriangle,
  RefreshCw,
  Download,
  Target,
  FileSpreadsheet,
  Star,
  XCircle,
  Package,
  Clock,
  CheckCircle
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { useLanguage } from '../contexts/LanguageContext';
import TranslatedText from '../components/TranslatedText';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  Radar
} from 'recharts';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

const VendorLinkDashboard = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(null);
  const { languageConfig } = useLanguage();

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${BACKEND_URL}/api/agent-dashboards/vendor-link`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      setData(response.data);
    } catch (error) {
      console.error('Failed to load dashboard:', error);
      toast.error('Failed to load VendorLink dashboard');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleExport = async (dataType, label) => {
    setExporting(dataType);
    try {
      const response = await axios.get(
        `${BACKEND_URL}/api/agent-dashboards/vendor-link/export`,
        {
          params: { data_type: dataType },
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
          responseType: 'blob'
        }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.download = `${dataType}_${new Date().toISOString().split('T')[0]}.xlsx`;
      link.click();
      toast.success(`${label} exported successfully`);
    } catch (error) {
      toast.error(`Failed to export ${label}`);
    } finally {
      setExporting(null);
    }
  };

  const handleExportAll = async (dataType, label) => {
    setExporting(dataType);
    try {
      const response = await axios.get(
        `${BACKEND_URL}/api/agent-dashboards/vendor-link/export-all`,
        {
          params: { data_type: dataType },
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
          responseType: 'blob'
        }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.download = `vendor_link_${dataType}_${new Date().toISOString().split('T')[0]}.xlsx`;
      link.click();
      toast.success(`${label} exported successfully`);
    } catch (error) {
      toast.error(`Failed to export ${label}`);
    } finally {
      setExporting(null);
    }
  };

  const formatCurrency = (value) => {
    if (typeof value === 'string') return value;
    if (value >= 10000000) return `₹${(value / 10000000).toFixed(2)} Cr`;
    if (value >= 100000) return `₹${(value / 100000).toFixed(2)} L`;
    if (value >= 1000) return `₹${(value / 1000).toFixed(1)} K`;
    return `₹${value?.toFixed(0) || 0}`;
  };

  const formatNumber = (value) => {
    if (typeof value === 'string') return value;
    return value?.toLocaleString() || '0';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]" data-testid="vendor-link-loading">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-600"></div>
      </div>
    );
  }

  if (!data?.hasData) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] space-y-4" data-testid="vendor-link-no-data">
        <Truck className="h-16 w-16 text-slate-400" />
        <h2 className="text-xl font-semibold text-slate-700">No Analysis Available</h2>
        <p className="text-slate-500">{data?.message || 'Please run the ReasonVendorLink agent first.'}</p>
        <Button onClick={() => window.location.href = '/agents'}>Go to Agents</Button>
      </div>
    );
  }

  const overview = data?.vendorOverview || {};
  const vendorScorecard = data?.vendorScorecard || [];
  const topVendors = data?.topVendors || [];
  const problemVendors = data?.problemVendors || [];
  const qualityAnalysis = data?.qualityAnalysis || {};
  const deadStockByVendor = data?.deadStockByVendor || [];
  const dependencyRisk = data?.dependencyRisk || {};
  const recommendations = data?.recommendations || [];
  const actionPlan = data?.actionPlan || {};

  const COLORS = ['#06b6d4', '#22c55e', '#3b82f6', '#f97316', '#ef4444', '#8b5cf6'];

  return (
    <div className="space-y-6" data-testid="vendor-link-dashboard">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 dark:text-white flex items-center gap-2">
            <Truck className="h-8 w-8 text-cyan-600" />
            <TranslatedText text="VendorLink Dashboard" cacheKey="vendorlink:title" />
          </h1>
          <p className="text-slate-600 dark:text-slate-400 mt-1" style={{ fontFamily: languageConfig?.font }}>
            <TranslatedText text="Vendor Performance & Supply Chain Intelligence" cacheKey="vendorlink:subtitle" />
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={loadData}>
            <RefreshCw className="h-4 w-4 mr-2" />Refresh
          </Button>
          <Button variant="outline" size="sm" onClick={() => handleExportAll('all_analysis', 'Full Analysis')}>
            <Download className="h-4 w-4 mr-2" />Export All
          </Button>
        </div>
      </div>

      {/* Executive Summary */}
      <Card className="bg-gradient-to-r from-cyan-50 to-teal-50 dark:from-cyan-900/20 border-cyan-200">
        <CardHeader><CardTitle><TranslatedText text="Executive Summary" cacheKey="vendorlink:execsummary" /></CardTitle></CardHeader>
        <CardContent>
          <p className="text-slate-700 dark:text-slate-300 whitespace-pre-line" style={{ fontFamily: languageConfig?.font }}>
            <TranslatedText text={data?.summary} cacheKey="vendorlink:summary" />
          </p>
        </CardContent>
      </Card>

      {/* Key Metrics */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="bg-gradient-to-br from-cyan-50 to-cyan-100 border-cyan-200">
          <CardContent className="pt-4">
            <p className="text-xs text-cyan-700" style={{ fontFamily: languageConfig?.font }}><TranslatedText text="Total Vendors" cacheKey="vendorlink:total_vendors" /></p>
            <p className="text-xl font-bold text-cyan-900">{formatNumber(overview.totalVendors)}</p>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-green-50 to-green-100 border-green-200">
          <CardContent className="pt-4">
            <p className="text-xs text-green-700" style={{ fontFamily: languageConfig?.font }}><TranslatedText text="Top Performers" cacheKey="vendorlink:top_performers" /></p>
            <p className="text-xl font-bold text-green-900">{topVendors.length}</p>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-red-50 to-red-100 border-red-200">
          <CardContent className="pt-4">
            <p className="text-xs text-red-700" style={{ fontFamily: languageConfig?.font }}><TranslatedText text="Problem Vendors" cacheKey="vendorlink:problem_vendors" /></p>
            <p className="text-xl font-bold text-red-900">{problemVendors.length}</p>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-orange-50 to-orange-100 border-orange-200">
          <CardContent className="pt-4">
            <p className="text-xs text-orange-700" style={{ fontFamily: languageConfig?.font }}><TranslatedText text="Dead Stock Vendors" cacheKey="vendorlink:dead_stock" /></p>
            <p className="text-xl font-bold text-orange-900">{deadStockByVendor.length}</p>
          </CardContent>
        </Card>
      </div>

      {/* Top 5 Recommendations */}
      <Card className="border-cyan-200 bg-cyan-50/30">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2 text-cyan-800">
              <Target className="h-5 w-5" />
              <TranslatedText text="Top 5 Vendor Recommendations" cacheKey="vendorlink:recommendations_title" />
            </CardTitle>
            <Button size="sm" variant="outline" onClick={() => handleExportAll('recommendations', 'All Recommendations')} disabled={exporting === 'recommendations'}>
              <FileSpreadsheet className="h-4 w-4 mr-1" />Download All
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {recommendations.slice(0, 5).map((rec, idx) => (
              <div key={idx} className="flex items-start gap-3 p-3 bg-white dark:bg-slate-800 rounded-lg border">
                <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold text-white ${
                  idx === 0 ? 'bg-cyan-500' : idx === 1 ? 'bg-blue-500' : 'bg-slate-500'
                }`}>{idx + 1}</span>
                <div className="flex-1">
                  <p className="font-medium">{rec.description || rec.action || rec.text || rec}</p>
                  {rec.expectedImpact && <p className="text-sm text-cyan-600 mt-1">Impact: {rec.expectedImpact}</p>}
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Top vs Problem Vendors */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Top Vendors */}
        <Card className="border-green-200">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-green-700 flex items-center gap-2">
                <Star className="h-5 w-5" />
                Top Performing Vendors
              </CardTitle>
              <Button size="sm" variant="outline" onClick={() => handleExport('vendor_scorecard', 'Vendor Scorecard')} disabled={exporting === 'vendor_scorecard'}>
                <FileSpreadsheet className="h-4 w-4 mr-1" />Export All
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-2 max-h-64 overflow-y-auto">
              {topVendors.slice(0, 10).map((vendor, idx) => (
                <div key={idx} className="flex items-center justify-between p-3 bg-green-50 rounded-lg border border-green-100">
                  <div className="flex items-center gap-3">
                    <span className="w-8 h-8 rounded-full bg-green-200 flex items-center justify-center text-green-700 font-bold text-sm">
                      {idx + 1}
                    </span>
                    <div>
                      <p className="font-medium">{vendor.vendorName || vendor.vendorId}</p>
                      <p className="text-xs text-slate-500">{vendor.category || 'General'}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="font-semibold text-green-600">{formatCurrency(vendor.revenue || vendor.totalValue)}</p>
                    {vendor.score && <p className="text-xs text-slate-500">Score: {vendor.score}</p>}
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Problem Vendors */}
        <Card className="border-red-200">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-red-700 flex items-center gap-2">
                <XCircle className="h-5 w-5" />
                Problem Vendors
              </CardTitle>
              <Button size="sm" variant="outline" onClick={() => handleExport('problem_vendors', 'Problem Vendors')} disabled={exporting === 'problem_vendors'}>
                <FileSpreadsheet className="h-4 w-4 mr-1" />Export All
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-2 max-h-64 overflow-y-auto">
              {problemVendors.slice(0, 10).map((vendor, idx) => (
                <div key={idx} className="flex items-center justify-between p-3 bg-red-50 rounded-lg border border-red-100">
                  <div className="flex items-center gap-3">
                    <span className="w-8 h-8 rounded-full bg-red-200 flex items-center justify-center text-red-700 font-bold text-sm">
                      !
                    </span>
                    <div>
                      <p className="font-medium">{vendor.vendorName || vendor.vendorId}</p>
                      <p className="text-xs text-red-600">{vendor.issue || vendor.reason || 'Performance issue'}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    {vendor.returnRate && <p className="text-sm text-red-600">Return: {vendor.returnRate}</p>}
                    {vendor.delayRate && <p className="text-sm text-orange-600">Delay: {vendor.delayRate}</p>}
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Dead Stock by Vendor */}
      {deadStockByVendor.length > 0 && (
        <Card className="border-orange-200">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-orange-700 flex items-center gap-2">
                <Package className="h-5 w-5" />
                Dead Stock by Vendor (Blocked Capital)
              </CardTitle>
              <Button size="sm" variant="outline" onClick={() => handleExport('dead_stock_vendors', 'Dead Stock by Vendor')} disabled={exporting === 'dead_stock_vendors'}>
                <FileSpreadsheet className="h-4 w-4 mr-1" />Export All
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead className="bg-orange-50">
                  <tr>
                    <th className="px-3 py-2 text-left font-medium">Vendor</th>
                    <th className="px-3 py-2 text-right font-medium">Dead SKUs</th>
                    <th className="px-3 py-2 text-right font-medium">Blocked Value</th>
                    <th className="px-3 py-2 text-right font-medium">Age (Days)</th>
                    <th className="px-3 py-2 text-left font-medium">Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {deadStockByVendor.slice(0, 10).map((vendor, idx) => (
                    <tr key={idx} className="hover:bg-orange-50/50">
                      <td className="px-3 py-2 font-medium">{vendor.vendorName || vendor.vendorId}</td>
                      <td className="px-3 py-2 text-right text-red-600">{vendor.deadSKUs || vendor.skuCount}</td>
                      <td className="px-3 py-2 text-right text-orange-600 font-medium">{formatCurrency(vendor.blockedValue || vendor.value)}</td>
                      <td className="px-3 py-2 text-right">{vendor.avgAge || vendor.ageDays || '-'}</td>
                      <td className="px-3 py-2 text-slate-500 text-xs">{vendor.recommendation || vendor.action || 'Return/Liquidate'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Dependency Risk */}
      {Object.keys(dependencyRisk).length > 0 && (
        <Card className="border-yellow-200">
          <CardHeader>
            <CardTitle className="text-yellow-700 flex items-center gap-2">
              <AlertTriangle className="h-5 w-5" />
              Vendor Dependency Risk
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="p-3 bg-yellow-50 rounded-lg">
                <p className="text-xs text-yellow-700">Top Vendor Concentration</p>
                <p className="text-lg font-bold text-yellow-900">{dependencyRisk.topVendorShare || 'N/A'}</p>
              </div>
              <div className="p-3 bg-yellow-50 rounded-lg">
                <p className="text-xs text-yellow-700">Single-Source SKUs</p>
                <p className="text-lg font-bold text-yellow-900">{dependencyRisk.singleSourceSKUs || 'N/A'}</p>
              </div>
              <div className="p-3 bg-yellow-50 rounded-lg">
                <p className="text-xs text-yellow-700">High Risk Categories</p>
                <p className="text-lg font-bold text-yellow-900">{dependencyRisk.highRiskCategories || 'N/A'}</p>
              </div>
              <div className="p-3 bg-yellow-50 rounded-lg">
                <p className="text-xs text-yellow-700">Risk Level</p>
                <p className="text-lg font-bold text-yellow-900">{dependencyRisk.overallRisk || 'N/A'}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Action Plan */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>Prioritized Action Plan</CardTitle>
            <Button size="sm" variant="outline" onClick={() => handleExportAll('action_plan', 'Action Plan')} disabled={exporting === 'action_plan'}>
              <FileSpreadsheet className="h-4 w-4 mr-1" />Export
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="p-4 bg-red-50 rounded-lg border-l-4 border-red-500">
              <h4 className="font-bold text-red-800 mb-3">P0 - Immediate</h4>
              <ul className="space-y-2 text-sm">
                {(actionPlan.P0_Immediate || []).slice(0, 3).map((action, idx) => (
                  <li key={idx} className="flex items-start gap-2">
                    <span className="text-red-500 mt-1">•</span>
                    <span>{action.action || action}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="p-4 bg-yellow-50 rounded-lg border-l-4 border-yellow-500">
              <h4 className="font-bold text-yellow-800 mb-3">P1 - This Week</h4>
              <ul className="space-y-2 text-sm">
                {(actionPlan.P1_ThisWeek || []).slice(0, 3).map((action, idx) => (
                  <li key={idx} className="flex items-start gap-2">
                    <span className="text-yellow-500 mt-1">•</span>
                    <span>{action.action || action}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="p-4 bg-green-50 rounded-lg border-l-4 border-green-500">
              <h4 className="font-bold text-green-800 mb-3">P2 - Next Cycle</h4>
              <ul className="space-y-2 text-sm">
                {(actionPlan.P2_NextCycle || []).slice(0, 3).map((action, idx) => (
                  <li key={idx} className="flex items-start gap-2">
                    <span className="text-green-500 mt-1">•</span>
                    <span>{action.action || action}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default VendorLinkDashboard;
